import type {ReactElement} from 'react';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {InputFieldText, InputFieldTextType} from '@Components/input-field-v2/components/input-field-text';
import {Text, TextSize} from '@Components/text';
import {Tooltip} from '@Components/tooltip';
import {useCopyToClipboardWithFeedback} from '@Hooks/useCopyToClipboardWithFeedback';
import {useIsCompactQRTool} from '@Components/qr-code-generator/qr-code-generator-hooks';
import {QRCopyResult} from '@Components/qr-code-generator/qr-generator-tracking.library';
import {getUtmExampleLink} from '@Components/utm-link-builder/utm-link-builder.library';
import styles from './utm-link-preview.module.scss';
import type {UtmLinkPreviewProps} from './utm-link-preview.types';
import {UTM_COPIED_TOOLTIP_ID} from './utm-link-preview.types';

/**
 * The link as it is built, updated on every keystroke, in a box drawn like the form's inputs, with what Copy needs always written under
 * it the way the inputs carry their help text, and the two things the visitor can do with the link below that.
 */
export function UtmLinkPreview({previewLink, completeLink, canClear, onClear, onCopyResult}: UtmLinkPreviewProps): ReactElement {
  const {onCopy, doShowFeedback} = useCopyToClipboardWithFeedback(completeLink ?? '', {
    onCopied: (): void => {
      onCopyResult(QRCopyResult.OK);
    },
    onCopyFailed: (): void => {
      onCopyResult(QRCopyResult.FAILED);
    },
  });
  const isCompact = useIsCompactQRTool();

  /** Shown only once the clipboard took the link, so a failed copy never claims success. */
  const renderCopiedTooltip = (): ReactElement | null => {
    if (!doShowFeedback) {
      return null;
    }
    return (
      <Tooltip
        id={UTM_COPIED_TOOLTIP_ID}
        className={styles.utmLinkPreviewCopiedTooltip}
        text={<Text val={window.i18next.t('pmwjs_copied')} size={TextSize.SM} attributes={{role: 'status'}} />}
      />
    );
  };

  return (
    <section className={styles.utmLinkPreview} aria-label={window.i18next.t('pmwjs_utm_builder_preview_heading')}>
      <div className={styles.utmLinkPreviewField}>
        <Text
          val={window.i18next.t('pmwjs_utm_builder_preview_heading')}
          size={isCompact ? TextSize.SM : TextSize.MD}
          bold
          className={styles.utmLinkPreviewHeading}
        />
        <output className={styles.utmLinkPreviewOutput} aria-live="polite">
          <Text
            val={previewLink ?? getUtmExampleLink()}
            size={TextSize.MD}
            className={previewLink === null ? styles.utmLinkPreviewPlaceholder : styles.utmLinkPreviewLink}
          />
        </output>
        <InputFieldText
          text={window.i18next.t('pmwjs_utm_builder_preview_incomplete')}
          type={InputFieldTextType.DEFAULT_INFORMATION_TEXT}
          className={styles.utmLinkPreviewInformation}
        />
      </div>

      <div className={styles.utmLinkPreviewActions}>
        <div className={styles.utmLinkPreviewAction}>
          {renderCopiedTooltip()}
          <Button
            text={window.i18next.t('pmwjs_copy')}
            type={ButtonType.PRIMARY}
            size={ButtonSize.MEDIUM}
            icon="icon-copy"
            disabled={completeLink === null}
            isFullWidth
            customClasses={styles.utmLinkPreviewActionButton}
            onClick={onCopy}
          />
        </div>
        <div className={styles.utmLinkPreviewAction}>
          <Button
            text={window.i18next.t('pmwjs_clear')}
            type={ButtonType.SECONDARY}
            size={ButtonSize.MEDIUM}
            icon="icon-rotate-ccw"
            disabled={!canClear}
            isFullWidth
            customClasses={styles.utmLinkPreviewActionButton}
            onClick={onClear}
          />
        </div>
      </div>
    </section>
  );
}
